import React, { useContext, useEffect, useRef, useState } from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import { LayoutContext } from 'providers/layout.provider'
import {
  HeaderWrapper,
  Logo,
  MenuToggle,
  MenuClip,
  MenuPanel,
  MenuList,
  MenuListItem,
  MenuRow,
  MenuLink,
  SubMenuToggle,
  SubMenuWrap,
  SubMenu,
  SubMenuItem,
  SubMenuLink,
  MenuFooter
} from './index.styled'
import FooterContent from 'components/footer-content'
import LogoWordmark from 'icons/logo_wordmark.svg'
import LogoBrandmark from 'icons/logo_brandmark.svg'
import IcoClose from 'icons/ico_close.svg'
import IcoCloseMobile from 'icons/ico_close_mobile.svg'

const Header = () => {
  const { headerHidden, mobile, scrollSmoother } = useContext(LayoutContext)
  const [open, setOpen] = useState(false)
  // Index of the menu item whose sub-menu is toggled open (click/tap controlled,
  // so sub-menus also work on touch where there is no hover). null = none.
  const [openSubIndex, setOpenSubIndex] = useState(null)
  const toggleRef = useRef(null)

  // Menu links come from the global `Menu` Single Type in Strapi.
  const { strapiMenu } = useStaticQuery(graphql`
    query MenuQuery {
      strapiMenu {
        Items {
          id
          Link {
            Title
            Type
            Page {
              Path
            }
            URL
            File {
              localFile {
                publicURL
              }
            }
            SectionId
          }
          SubItems {
            id
            Title
            Type
            Page {
              Path
            }
            URL
            File {
              localFile {
                publicURL
              }
            }
            SectionId
          }
        }
      }
    }
  `)

  const items = strapiMenu?.Items || []

  const closeMenu = () => {
    setOpen(false)
    setOpenSubIndex(null)
  }

  // Toggle the panel, collapsing any open sub-menu when closing.
  const toggleMenu = () => {
    if (open) setOpenSubIndex(null)
    setOpen((prev) => !prev)
  }

  // A tap on any link inside the mobile menu footer also dismisses the panel.
  const handleFooterClick = (event) => {
    if (event.target.closest('a')) closeMenu()
  }

  // Close on Escape and return focus to the toggle button (WCAG 2.1.2).
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false)
        setOpenSubIndex(null)
        toggleRef.current?.focus()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // The mobile panel covers the viewport, so freeze the page behind it.
  useEffect(() => {
    if (!mobile) return
    const smoother = scrollSmoother.current
    if (!smoother) return
    smoother.paused(open)
    return () => smoother.paused(false)
  }, [open, mobile, scrollSmoother])

  return (
    <HeaderWrapper $hidden={headerHidden}>
      <MenuToggle
        ref={toggleRef}
        type='button'
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
        onClick={toggleMenu}
      >
        <img src={open ? (mobile ? IcoCloseMobile : IcoClose) : LogoBrandmark} alt='' />
      </MenuToggle>
      <Logo to='/'><img src={LogoWordmark} alt='Echelon Studios' /></Logo>

      <nav aria-label='Main navigation' aria-hidden={open ? undefined : true}>
        <MenuClip>
          <MenuPanel className={open ? 'open' : ''}>
            <MenuList>
              {items.map((item, index) => {
                const subItems = item.SubItems || []
                const hasSubMenu = subItems.length > 0
                const subOpen = openSubIndex === index
                return (
                  <MenuListItem key={item.id || index} $subOpen={subOpen}>
                    <MenuRow>
                      <MenuLink data={item.Link} onClick={closeMenu} />
                      {hasSubMenu && (
                        <SubMenuToggle
                          type='button'
                          aria-label={`${item.Link?.Title || 'Menu'} submenu`}
                          aria-expanded={subOpen}
                          onClick={() => setOpenSubIndex(subOpen ? null : index)}
                        />
                      )}
                    </MenuRow>
                    {hasSubMenu && (
                      <SubMenuWrap $open={subOpen}>
                        <SubMenu $open={subOpen}>
                          {subItems.map((subItem, subIndex) => (
                            <SubMenuItem key={subItem.id || subIndex}>
                              <SubMenuLink data={subItem} onClick={closeMenu} />
                            </SubMenuItem>
                          ))}
                        </SubMenu>
                      </SubMenuWrap>
                    )}
                  </MenuListItem>
                )
              })}
            </MenuList>
            <MenuFooter onClick={handleFooterClick}>
              <FooterContent />
            </MenuFooter>
          </MenuPanel>
        </MenuClip>
      </nav>
    </HeaderWrapper>
  )
}

export default Header
